import { useTranslation } from 'react-i18next';

import { CHANNEL_STATUS, PROTOCOL, TChannelProtocol, TChannelStatus } from '@/entities/channel';
import { Chip } from '@/shared/ui/Chip';
import { Loader } from '@/shared/ui/Loader';

import {
  CHANNEL_LIST_STATUS,
  getChannelListStatus,
  TChannelListStatus,
} from '../model/channelListStatus';
import { CHANNELS_STATUS_TO_I18_KEY } from '../model/constants';

type TChannelStatusProps = {
  status: TChannelStatus;
  protocol?: TChannelProtocol;
  isActive?: boolean;
  subscriptionEndDate?: string | null;
  subscriptionUnavailable?: boolean;
  variant?: 'full' | 'list';
};

const STATUS_COLORS = {
  [CHANNEL_STATUS.ACTIVE]: 'error',
  [CHANNEL_STATUS.INACTIVE]: 'error',
  [CHANNEL_STATUS.UNAUTHORIZED]: 'error',
  [CHANNEL_STATUS.PENDING_PAYMENT]: 'warning',
  [CHANNEL_STATUS.QR_IN_PROGRESS]: 'error',
  [CHANNEL_STATUS.AUTHORIZED]: 'info',
  [CHANNEL_STATUS.CONTACTS_SYNCED]: 'info',
  [CHANNEL_STATUS.CHATS_SYNCED]: 'info',
  [CHANNEL_STATUS.READY]: 'success',
  [CHANNEL_STATUS.PASSWORD_REQUIRED]: 'warning',
  [CHANNEL_STATUS.REQUIRED_SCAN_QR]: 'error',
  [CHANNEL_STATUS.SMS_SENT]: 'error',
  [CHANNEL_STATUS.SUSPENDED]: 'warning',
} as const;

const IN_PROGRESS_STATUSES = [
  CHANNEL_STATUS.AUTHORIZED,
  CHANNEL_STATUS.CONTACTS_SYNCED,
  CHANNEL_STATUS.CHATS_SYNCED,
] as Array<TChannelStatus>;

const LIST_STATUS_TO_CHIP: Record<
  TChannelListStatus,
  { labelKey: string; color: 'success' | 'warning' | 'error' | 'info' }
> = {
  [CHANNEL_LIST_STATUS.ACTIVE]: {
    labelKey: 'channels:status.ready',
    color: 'success',
  },
  [CHANNEL_LIST_STATUS.DISABLED]: {
    labelKey: 'channels:status.channelDisabled',
    color: 'warning',
  },
  [CHANNEL_LIST_STATUS.AWAITING_PAYMENT]: {
    labelKey: 'channels:status.pendingPayment',
    color: 'warning',
  },
  [CHANNEL_LIST_STATUS.SYNCING]: {
    labelKey: 'channels:status.syncingChats',
    color: 'info',
  },
  [CHANNEL_LIST_STATUS.AUTH_REQUIRED]: {
    labelKey: 'channels:status.authRequired',
    color: 'error',
  },
  [CHANNEL_LIST_STATUS.SUSPENDED]: {
    labelKey: 'channels:status.suspended',
    color: 'warning',
  },
  [CHANNEL_LIST_STATUS.SUBSCRIPTION_EXPIRING]: {
    labelKey: 'channels:status.subscriptionExpiring',
    color: 'warning',
  },
  [CHANNEL_LIST_STATUS.ENDED]: {
    labelKey: 'channels:status.ended',
    color: 'error',
  },
};

const getSyncingLabelKey = (status: TChannelStatus) => {
  if (status === CHANNEL_STATUS.AUTHORIZED) {
    return 'channels:status.syncingContacts';
  }

  return 'channels:status.syncingChats';
};

export const ChannelStatus = ({
  status,
  protocol,
  isActive,
  subscriptionEndDate,
  subscriptionUnavailable,
  variant = 'full',
}: TChannelStatusProps) => {
  const { t } = useTranslation();
  const suspendedTitle = status !== CHANNEL_STATUS.SUSPENDED
    ? undefined
    : protocol === PROTOCOL.AVITO
      ? t('channels:status.suspendedAvitoReason')
      : protocol === PROTOCOL.MAX || protocol === PROTOCOL.MAX_PERSONAL
        ? t('channels:status.suspendedMaxReason')
        : t('channels:status.suspendedReason');

  if (variant === 'list') {
    const listStatus = getChannelListStatus({
      status,
      isActive,
      subscriptionEndDate,
      subscriptionUnavailable,
    });
    const chip = LIST_STATUS_TO_CHIP[listStatus];
    const isSyncing = listStatus === CHANNEL_LIST_STATUS.SYNCING;

    return (
      <Chip
        label={t(isSyncing ? getSyncingLabelKey(status) : chip.labelKey)}
        color={chip.color}
        size="small"
        title={suspendedTitle}
        icon={
          isSyncing ? (
            <Loader
              size="10px"
              containerSize="auto"
              sx={{
                width: '15px',
                display: 'flex',
                alignItems: 'center',
                justifyItems: 'center',
                color: 'info.main',
              }}
            />
          ) : undefined
        }
      />
    );
  }

  return (
    <Chip
      label={t(CHANNELS_STATUS_TO_I18_KEY[status])}
      color={STATUS_COLORS[status]}
      size="small"
      title={suspendedTitle}
      icon={
        IN_PROGRESS_STATUSES.includes(status) ? (
          <Loader
            size="10px"
            containerSize="auto"
            sx={{
              width: '15px',
              display: 'flex',
              alignItems: 'center',
              justifyItems: 'center',
            }}
          />
        ) : undefined
      }
    />
  );
};
